import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { ScanData } from 'pages/reporting/reports/raut/sections/subSections/indicationDetailsComponents/indicationDisplay/types'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseWeldScanFilesProps {
  id: string
}

/**
 * Return type for weld scan files
 */
export interface WeldScanFiles {
  /** Composite S-scan image from Maxweld */
  sscanImageUrl?: string
  /** Top view - along-weld vs cross-weld */
  topView?: ScanData
  /** Side view - depth vs along-weld */
  sideView?: ScanData
}

/**
 * Hook to fetch and process scan files for weld indications
 *
 * This hook:
 * - Fetches scan files (sscan.png, topview.json, sideview.json)
 * - Processes raw scan data
 * - Provides loading states and error handling
 *
 * @param props - Hook properties
 * @returns Processed scan data for S-scan image, top view, and side view
 */
export const useWeldScanFiles = ({ id }: UseWeldScanFilesProps) => {
  const [scanData, setScanData] = useState<{
    sscanImageUrl?: string
    topView?: number[][]
    sideView?: number[][]
  }>({})

  const [error, setError] = useState<Error>()

  /**
   * Cleans and parses raw scan data text (for top view and side view JSON)
   */
  const cleanAndParseScanData = useCallback((text: string): number[][] => {
    try {
      const cleanedText = text
        .replace(/\bNaN\b/g, '0')
        .replace(/(\d+\.\d+)e-(\d+)/g, '0')
        .replace(/\s+/g, ' ')
        .trim()

      if (!cleanedText.startsWith('[[') || !cleanedText.endsWith(']]')) {
        throw new Error('Invalid data format')
      }

      const parsed = JSON.parse(cleanedText) as (
        | number
        | string
        | null
        | undefined
      )[][]

      return parsed.map((row) =>
        row.map((value) => {
          const num = Number(value)
          return Number.isFinite(num) ? num : 0
        })
      )
    } catch (e) {
      console.error('Error cleaning/parsing data:', e)
      return []
    }
  }, [])

  /** Get file references for scan files */
  const { fileRefs, isLoading: fileRefsLoading } = useRolodexFileLS({
    entry_id: id,
    path: '/',
  })

  /**
   * Map file paths to scan types
   */
  const scanRefs = useMemo(() => {
    const findRef = (patterns: string[]) => {
      return fileRefs.find((ref) =>
        patterns.some((p) => ref.path.toLowerCase() === p.toLowerCase())
      )
    }

    return {
      // S-scan composite image from paut-display (contains left + merged + right)
      sscanImage: findRef(['sscan.png']),
      // Top view and side view JSON data
      topView: findRef(['topview.json']),
      sideView: findRef(['sideview.json']),
    }
  }, [fileRefs])

  /** Get signed URLs for available scan files */
  const urlRequests = useMemo(() => {
    return Object.entries(scanRefs)
      .filter(([, ref]) => ref !== undefined)
      .map(([, ref]) => ({
        entry_id: id,
        path: ref!.path,
      }))
  }, [id, scanRefs])

  const { results: signedUrls, isLoading: urlsLoading } =
    useRolodexSignedURLs(urlRequests)

  /**
   * Fetch and process scan data when signed URLs are available
   */
  useEffect(() => {
    let isMounted = true

    const fetchScanData = async () => {
      try {
        const newScanData: typeof scanData = {}

        // Fetch top view and side view JSON data
        for (const scanType of ['topView', 'sideView'] as const) {
          const ref = scanRefs[scanType]
          if (!ref) continue

          const url = signedUrls.find(
            (urlResult) => urlResult.path === ref.path
          )?.signed_url

          if (url) {
            try {
              const response = await fetch(url)
              const text = await response.text()
              const processedData = cleanAndParseScanData(text)

              if (processedData.length > 0 && isMounted) {
                newScanData[scanType] = processedData
              }
            } catch (fetchError) {
              console.error(
                `Error fetching ${scanType} for entry ${id}:`,
                fetchError
              )
            }
          }
        }

        // Get signed URL for S-scan image (no need to fetch, just store the URL)
        if (scanRefs.sscanImage) {
          const url = signedUrls.find(
            (urlResult) => urlResult.path === scanRefs.sscanImage!.path
          )?.signed_url
          if (url) {
            newScanData.sscanImageUrl = url
          }
        }

        if (isMounted) {
          setScanData(newScanData)
        }
      } catch (e) {
        console.error(`Error processing weld scan data for entry ${id}:`, e)
        if (isMounted) {
          setError(
            e instanceof Error ? e : new Error('Failed to fetch scan data')
          )
        }
      }
    }

    if (signedUrls.length > 0) {
      fetchScanData()
    }

    return () => {
      isMounted = false
    }
  }, [id, signedUrls, scanRefs, cleanAndParseScanData])

  const scanFiles: WeldScanFiles = useMemo(
    () => ({
      sscanImageUrl: scanData.sscanImageUrl,
      topView: scanData.topView ? { data: scanData.topView } : undefined,
      sideView: scanData.sideView ? { data: scanData.sideView } : undefined,
    }),
    [scanData]
  )

  return {
    scanFiles,
    isLoading: fileRefsLoading || urlsLoading,
    error,
    hasScanData:
      !!scanData.sscanImageUrl || !!scanData.topView || !!scanData.sideView,
  }
}
